import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, TextInput } from 'flowbite-react';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { find, flattenDeep, map, uniq } from 'lodash';
import { toast } from 'react-toastify';
import SpinnerComponent from 'components/spinner';
import { getOrganizationById, getOrganizationIntegrationConfig, updateOrganizationIntegrationConfig } from 'api/organizationApi';
import { orgSelector, userSelector } from 'redux/selectors';
import { integrationVendors, permissionKeys } from 'utils/constants';
import { checkOrgAdmin, messageErrors } from 'utils/utils';

const MASKED_SECRET_VALUE = '••••••••••••';

type AbcIntegrationFormState = {
  siteUrl: string,
  appId: string,
  appKey: string,
  hasAppId: boolean,
  hasAppKey: boolean,
};

const PosIntegrationPage = () => {
  const [t] = useTranslation();
  const { organizationId } = useSelector(orgSelector);
  const { userInfo } = useSelector(userSelector);
  const { WRITE_ORG, PLATFORM_ADMIN } = permissionKeys;

  const siteAdmin = find(uniq(flattenDeep(map(userInfo?.roles, (r: any) => r?.permissions))), (per: any) =>
    [WRITE_ORG, PLATFORM_ADMIN].includes(per),
  );
  const isOrgAdmin = checkOrgAdmin(userInfo, organizationId, !!siteAdmin);

  const [abcIntegration, setAbcIntegration] = useState<AbcIntegrationFormState>({
    siteUrl: '',
    appId: '',
    appKey: '',
    hasAppId: false,
    hasAppKey: false,
  });
  const [isEditingAppId, setIsEditingAppId] = useState(false);
  const [isEditingAppKey, setIsEditingAppKey] = useState(false);

  const { data: organizationData, isLoading: isLoadingOrganization } = useQuery(
    ['getOrganizationDetailForPosIntegration', organizationId],
    () => getOrganizationById(organizationId),
    { enabled: !!organizationId, staleTime: 0 },
  );

  const assignedVendors = organizationData?.data?.assignedIntegrationVendors || [];
  const isAbcAssigned = assignedVendors.includes(integrationVendors.ABC_IGNITE);

  const { data: abcIntegrationData, isLoading: isLoadingAbc } = useQuery(
    ['getOrganizationAbcIntegrationConfig', organizationId],
    () => getOrganizationIntegrationConfig(organizationId, integrationVendors.ABC_IGNITE),
    { enabled: !!organizationId && isAbcAssigned },
  );

  useEffect(() => {
    if (abcIntegrationData?.data) {
      setAbcIntegration({
        siteUrl: abcIntegrationData.data.siteUrl || '',
        appId: '',
        appKey: '',
        hasAppId: !!abcIntegrationData.data.hasAppId,
        hasAppKey: !!abcIntegrationData.data.hasAppKey,
      });
      setIsEditingAppId(false);
      setIsEditingAppKey(false);
    }
  }, [abcIntegrationData]);

  const updateAbcIntegrationConfig = useMutation(['updateAbcIntegrationConfig'], {
    mutationFn: (payload: any) => updateOrganizationIntegrationConfig(organizationId, integrationVendors.ABC_IGNITE, payload),
    onSuccess: (response: any) => {
      const config = response?.data || {};
      setAbcIntegration({
        siteUrl: config.siteUrl || '',
        appId: '',
        appKey: '',
        hasAppId: !!config.hasAppId,
        hasAppKey: !!config.hasAppKey,
      });
      setIsEditingAppId(false);
      setIsEditingAppKey(false);
      toast.success('ABC Ignite settings updated successfully');
    },
    onError: (error: any) => {
      toast.error(messageErrors(error, t));
    },
  });

  const unlockSecretField = (field: 'appId' | 'appKey') => {
    setAbcIntegration((current) => ({
      ...current,
      [field]: '',
    }));

    if (field === 'appId') {
      setIsEditingAppId(true);
      return;
    }

    setIsEditingAppKey(true);
  };

  if (isLoadingOrganization || (isAbcAssigned && isLoadingAbc)) {
    return <SpinnerComponent />;
  }

  return (
    <div className="main-sub">
      <div className="bg-white w-full p-8 border border-gray-200 rounded-lg">
        <div className="text-xl font-semibold text-gray-900 pb-4">POS Integration</div>
        {!isAbcAssigned ? (
          <p className="text-gray-600">
            ABC Ignite is not assigned for this organization. Assign it from the Organization page under Assigned POS Integrations.
          </p>
        ) : (
          <div>
            <div className="text-lg font-semibold text-gray-900">ABC Ignite</div>
            <div className="text-sm text-gray-600 mt-1">ABC Fitness/Ignite credentials for this organization.</div>
            {!isOrgAdmin ? (
              <p className="text-sm text-gray-600 mt-4">
                You do not have permission to update these settings.
              </p>
            ) : (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
                  <div>
                    <label htmlFor="abc-site-url" className="text-sm text-gray-700">Site URL</label>
                    <TextInput
                      id="abc-site-url"
                      className="mt-1"
                      type="text"
                      value={abcIntegration.siteUrl}
                      onChange={(e) => setAbcIntegration({ ...abcIntegration, siteUrl: e.target.value })}
                    />
                  </div>
                  <div>
                    <label htmlFor="abc-app-id" className="text-sm text-gray-700">App ID</label>
                    <div className="mt-1 flex items-center gap-2">
                      <div className="flex-1">
                        <TextInput
                          id="abc-app-id"
                          type={abcIntegration.hasAppId && !isEditingAppId ? 'password' : 'text'}
                          value={abcIntegration.hasAppId && !isEditingAppId ? MASKED_SECRET_VALUE : abcIntegration.appId}
                          readOnly={abcIntegration.hasAppId && !isEditingAppId}
                          onChange={(e) => setAbcIntegration({ ...abcIntegration, appId: e.target.value })}
                        />
                      </div>
                      {abcIntegration.hasAppId && !isEditingAppId ? (
                        <button
                          type="button"
                          aria-label="Edit App ID"
                          className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 text-gray-600 transition hover:bg-gray-50 hover:text-gray-900"
                          onClick={() => unlockSecretField('appId')}
                        >
                          <PencilSquareIcon className="h-5 w-5" />
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <div>
                    <label htmlFor="abc-app-key" className="text-sm text-gray-700">App Key</label>
                    <div className="mt-1 flex items-center gap-2">
                      <div className="flex-1">
                        <TextInput
                          id="abc-app-key"
                          type={abcIntegration.hasAppKey && !isEditingAppKey ? 'password' : 'text'}
                          value={abcIntegration.hasAppKey && !isEditingAppKey ? MASKED_SECRET_VALUE : abcIntegration.appKey}
                          readOnly={abcIntegration.hasAppKey && !isEditingAppKey}
                          onChange={(e) => setAbcIntegration({ ...abcIntegration, appKey: e.target.value })}
                        />
                      </div>
                      {abcIntegration.hasAppKey && !isEditingAppKey ? (
                        <button
                          type="button"
                          aria-label="Edit App Key"
                          className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 text-gray-600 transition hover:bg-gray-50 hover:text-gray-900"
                          onClick={() => unlockSecretField('appKey')}
                        >
                          <PencilSquareIcon className="h-5 w-5" />
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className="mt-6">
                  <Button
                    type="button"
                    size="sm"
                    className="bg-blue-600 text-white"
                    onClick={() =>
                      updateAbcIntegrationConfig.mutate({
                        siteUrl: abcIntegration.siteUrl,
                        appId: abcIntegration.appId,
                        appKey: abcIntegration.appKey,
                      })
                    }
                    disabled={updateAbcIntegrationConfig.isLoading}
                  >
                    Save ABC Ignite Settings
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PosIntegrationPage;
